/* START_MODULE_CONTRACT
 * PURPOSE: localStorage feature overrides + gate for attaching X-Feature-Overrides header.
 * SCOPE: read/write feature_overrides; overrideAllowed for ADMIN only.
 * DEPENDS: M-FE-FEATURE-TOGGLE
 * LINKS: M-FE-FEATURE-TOGGLE, V-M-FE-FEATURE-TOGGLE, UC-022
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FEATURE_OVERRIDES_KEY - localStorage key
 * setFeatureOverrideAllowed - ADMIN gate for apiClient
 * isFeatureOverrideAllowed - current gate
 * readFeatureOverrides - parse localStorage
 * writeFeatureOverrides - persist map
 * getFeatureOverridesHeaderValue - JSON for X-Feature-Overrides or null
 * resolveIsEnabled - override (if allowed) else base
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022 feature toggle storage helpers.
 * END_CHANGE_SUMMARY
 */

export const FEATURE_OVERRIDES_KEY = 'feature_overrides';
export const FEATURE_OVERRIDE_HEADER = 'X-Feature-Overrides';
export const FLAG_ENABLE_REGISTRATION = 'enableRegistration';

let overrideAllowed = false;

/* START_METHOD_setFeatureOverrideAllowed */
/* START_CONTRACT: setFeatureOverrideAllowed
 * PURPOSE: Разрешить/запретить отправку оверрайдов (только ADMIN).
 * INPUTS: { allowed: boolean }
 * OUTPUTS: { void }
 * SIDE_EFFECTS: module-level flag.
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: setFeatureOverrideAllowed
 */
export function setFeatureOverrideAllowed(allowed) {
  overrideAllowed = Boolean(allowed);
}
/* END_METHOD_setFeatureOverrideAllowed */

/* START_METHOD_isFeatureOverrideAllowed */
/* START_CONTRACT: isFeatureOverrideAllowed
 * PURPOSE: Текущее состояние ADMIN gate.
 * INPUTS: нет
 * OUTPUTS: { boolean }
 * SIDE_EFFECTS: нет
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: isFeatureOverrideAllowed
 */
export function isFeatureOverrideAllowed() {
  return overrideAllowed;
}
/* END_METHOD_isFeatureOverrideAllowed */

/* START_METHOD_readFeatureOverrides */
/* START_CONTRACT: readFeatureOverrides
 * PURPOSE: Прочитать оверрайды из localStorage.
 * INPUTS: нет
 * OUTPUTS: { Record<string, boolean> }
 * SIDE_EFFECTS: localStorage read
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: readFeatureOverrides
 */
export function readFeatureOverrides() {
  try {
    const raw = localStorage.getItem(FEATURE_OVERRIDES_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}
/* END_METHOD_readFeatureOverrides */

/* START_METHOD_writeFeatureOverrides */
/* START_CONTRACT: writeFeatureOverrides
 * PURPOSE: Сохранить карту оверрайдов в localStorage.
 * INPUTS: { overrides: Record<string, boolean> }
 * OUTPUTS: { void }
 * SIDE_EFFECTS: localStorage write/remove
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: writeFeatureOverrides
 */
export function writeFeatureOverrides(overrides) {
  const keys = Object.keys(overrides || {});
  if (keys.length === 0) {
    localStorage.removeItem(FEATURE_OVERRIDES_KEY);
    return;
  }
  localStorage.setItem(FEATURE_OVERRIDES_KEY, JSON.stringify(overrides));
}
/* END_METHOD_writeFeatureOverrides */

/* START_METHOD_getFeatureOverridesHeaderValue */
/* START_CONTRACT: getFeatureOverridesHeaderValue
 * PURPOSE: JSON для заголовка, только если ADMIN и есть оверрайды.
 * INPUTS: нет
 * OUTPUTS: { string|null }
 * SIDE_EFFECTS: localStorage read
 * LINKS: M-FE-FEATURE-TOGGLE, M-FE-API
 * END_CONTRACT: getFeatureOverridesHeaderValue
 */
export function getFeatureOverridesHeaderValue() {
  if (!overrideAllowed) {
    return null;
  }
  const overrides = readFeatureOverrides();
  if (Object.keys(overrides).length === 0) {
    return null;
  }
  return JSON.stringify(overrides);
}
/* END_METHOD_getFeatureOverridesHeaderValue */

/* START_METHOD_resolveIsEnabled */
/* START_CONTRACT: resolveIsEnabled
 * PURPOSE: Приоритет: ADMIN override → base flags → false.
 * INPUTS: { flagName: string, baseFlags: Record<string, boolean> }
 * OUTPUTS: { boolean }
 * SIDE_EFFECTS: localStorage read when overrideAllowed
 * LINKS: M-FE-FEATURE-TOGGLE
 * END_CONTRACT: resolveIsEnabled
 */
export function resolveIsEnabled(flagName, baseFlags = {}) {
  if (overrideAllowed) {
    const overrides = readFeatureOverrides();
    if (Object.prototype.hasOwnProperty.call(overrides, flagName)) {
      return Boolean(overrides[flagName]);
    }
  }
  return Boolean(baseFlags[flagName]);
}
/* END_METHOD_resolveIsEnabled */
